'use client';

import { useEffect, useState } from 'react';
import { PaymentLogo, PAYMENT_METHOD_PAGES_CACHE_TTL_MS } from '@/constants';
import { defaultLocale } from '../i18n/config';
import { useLocalizedPath } from './useLocalizedPath';

type PageSlugMap = Record<string, Record<string, string>>;

const STORAGE_KEY = 'aceb_payment_method_pages';

let memoryCache: PageSlugMap | null = null;
let inflight: Promise<PageSlugMap> | null = null;

function readSessionCache(): PageSlugMap | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts?: number; pages?: PageSlugMap };
    if (
      typeof parsed?.ts !== 'number' ||
      !parsed.pages ||
      Date.now() - parsed.ts > PAYMENT_METHOD_PAGES_CACHE_TTL_MS
    ) {
      return null;
    }
    return parsed.pages;
  } catch {
    return null;
  }
}

function writeSessionCache(pages: PageSlugMap): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), pages }));
  } catch {
    // storage full or disabled
  }
}

function loadPaymentMethodPages(): Promise<PageSlugMap> {
  if (memoryCache) return Promise.resolve(memoryCache);

  const stored = readSessionCache();
  if (stored) {
    memoryCache = stored;
    return Promise.resolve(stored);
  }

  if (inflight) return inflight;

  inflight = fetch('/api/payment-method-pages')
    .then(async (res) => {
      if (!res.ok) throw new Error(`Payment method page probe failed: ${res.status}`);
      const body = (await res.json()) as { pages?: PageSlugMap };
      const pages = body.pages ?? {};
      if (Object.keys(pages).length > 0) {
        memoryCache = pages;
        writeSessionCache(pages);
      }
      return pages;
    })
    .catch((error) => {
      console.error('Error probing payment method pages:', error);
      return {} as PageSlugMap;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

export function usePaymentMethodPages() {
  const [pages, setPages] = useState<PageSlugMap>(() => memoryCache ?? {});
  const { locale, localizeFullPath } = useLocalizedPath();

  useEffect(() => {
    let active = true;
    loadPaymentMethodPages().then((resolved) => {
      if (active) setPages(resolved);
    });
    return () => {
      active = false;
    };
  }, []);

  const getPageHref = (logo: PaymentLogo): string | null => {
    if (!logo.pageSlug) return null;
    const slugs = pages[logo.pageSlug];
    if (!slugs) return null;
    const slug = slugs[locale] || slugs[defaultLocale] || logo.pageSlug;
    return `${localizeFullPath('/pages')}/${encodeURIComponent(slug)}`;
  };

  return { getPageHref };
}
